import { chalkLine, chalkText, getChalkRole } from '../../primitives/chalk';

function buildTreeLevels(rootId, edges) {
  const children = new Map();
  for (const edge of edges) {
    if (!children.has(edge.from)) children.set(edge.from, []);
    children.get(edge.from).push(edge.to);
  }

  const levels = new Map([[rootId, 0]]);
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.shift();
    const nextLevel = (levels.get(id) || 0) + 1;
    for (const child of children.get(id) || []) {
      if (!levels.has(child)) {
        levels.set(child, nextLevel);
        queue.push(child);
      }
    }
  }
  return levels;
}

function drawLabelBadge(ctx, text, x, y, size) {
  const safeText = String(text || '');
  if (!safeText) return;

  ctx.save();
  ctx.font = `${size}px sans-serif`;
  ctx.textAlign = 'center';
  const textWidth = Math.max(10, ctx.measureText(safeText).width);
  const padX = 8;
  const padY = 5;
  const width = textWidth + padX * 2;
  const height = size + padY * 2;
  const rectX = x - width / 2;
  const rectY = y - size * 0.8 - padY;

  ctx.fillStyle = 'rgba(10, 20, 34, 0.88)';
  ctx.strokeStyle = 'rgba(86, 182, 255, 0.72)';
  ctx.lineWidth = 1.2;
  ctx.fillRect(rectX, rectY, width, height);
  ctx.strokeRect(rectX, rectY, width, height);
  ctx.restore();

  chalkText(ctx, safeText, x, y, size, 0.98, 'sans-serif', 'center', { role: 'diagramLabel' });
}

export async function preparePhysicsDiagramNode(env, spec) {
  const area = env.boardArea();
  const titleSize = env.getStyle('bodySize');
  const labelSize = env.getStyle('smallSize');

  const durationSec = spec.durationSec ?? 7.5;
  const nodeRadius = spec.physics?.nodeRadius ?? 22;
  const restLength = spec.physics?.restLength ?? 96;

  const nodes = spec.diagram.nodes.map((node) => ({
    id: node.id,
    label: node.label,
    x: area.x + area.w / 2,
    y: area.y + area.h / 2,
    vx: 0,
    vy: 0,
    anchorX: area.x + area.w / 2,
    anchorY: area.y + area.h / 2,
    fixed: Boolean(node.fixed),
  }));

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const edges = spec.diagram.edges
    .map((edge) => {
      const from = nodeMap.get(edge.from);
      const to = nodeMap.get(edge.to);
      if (!from || !to) return null;
      return { from, to };
    })
    .filter(Boolean);

  if (spec.layout?.mode === 'tree' && spec.layout.rootId) {
    const levels = buildTreeLevels(spec.layout.rootId, spec.diagram.edges);
    const byLevel = new Map();
    for (const node of nodes) {
      const level = levels.get(node.id) ?? 0;
      if (!byLevel.has(level)) byLevel.set(level, []);
      byLevel.get(level).push(node);
    }

    const maxLevel = Math.max(...byLevel.keys());
    for (const [level, levelNodes] of byLevel.entries()) {
      const y =
        area.y + 70 + (maxLevel === 0 ? 0 : ((area.h - 130) * level) / Math.max(1, maxLevel));
      const spacing = area.w / (levelNodes.length + 1);
      levelNodes.forEach((node, idx) => {
        const x = area.x + spacing * (idx + 1);
        node.x = x;
        node.y = y;
        node.anchorX = x;
        node.anchorY = y;
      });
    }
  } else {
    nodes.forEach((node, i) => {
      const cols = Math.max(2, Math.ceil(Math.sqrt(nodes.length)));
      const row = Math.floor(i / cols);
      const col = i % cols;
      const spacingX = area.w / (cols + 1);
      const rows = Math.ceil(nodes.length / cols);
      const spacingY = area.h / (rows + 1);
      const x = area.x + spacingX * (col + 1);
      const y = area.y + spacingY * (row + 1);
      node.x = x;
      node.y = y;
      node.anchorX = x;
      node.anchorY = y;
    });
  }

  return {
    durationSec,
    title: spec.title,
    subtitle: spec.subtitle,
    titleX: area.x + area.w / 2,
    titleY: area.y + 28,
    titleSize,
    labelSize,
    area,
    nodes,
    edges,
    nodeRadius,
    restLength,
    springK: spec.physics?.springK ?? 8.5,
    damping: spec.physics?.damping ?? 0.84,
    repulsion: spec.physics?.repulsion ?? 9500,
    anchorK: spec.physics?.anchorK ?? 2.4,
    gravity: spec.physics?.gravity ?? 0,
  };
}

export function commitPhysicsDiagramNode(packet, ctxs, anim) {
  let t = 0;
  const nodeStrokeRole = getChalkRole('diagramNodeStroke');
  const nodeFillRole = getChalkRole('diagramNodeFill');

  anim.add((dt) => {
    t += dt;
    const clearPad = Math.max(12, packet.nodeRadius + packet.labelSize + 8);
    ctxs.ink.clearRect(
      packet.area.x - clearPad,
      packet.area.y - clearPad,
      packet.area.w + clearPad * 2,
      packet.area.h + clearPad * 2
    );

    const simDt = Math.min(0.03, Math.max(0.008, dt));

    for (let i = 0; i < packet.nodes.length; i++) {
      const a = packet.nodes[i];
      for (let j = i + 1; j < packet.nodes.length; j++) {
        const b = packet.nodes[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const distSq = dx * dx + dy * dy + 0.001;
        const dist = Math.sqrt(distSq);
        const f = packet.repulsion / distSq;
        const fx = (dx / dist) * f;
        const fy = (dy / dist) * f;
        if (!a.fixed) {
          a.vx -= fx * simDt;
          a.vy -= fy * simDt;
        }
        if (!b.fixed) {
          b.vx += fx * simDt;
          b.vy += fy * simDt;
        }
      }
    }

    for (const edge of packet.edges) {
      const dx = edge.to.x - edge.from.x;
      const dy = edge.to.y - edge.from.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.0001;
      const stretch = dist - packet.restLength;
      const f = packet.springK * stretch;
      const fx = (dx / dist) * f;
      const fy = (dy / dist) * f;

      if (!edge.from.fixed) {
        edge.from.vx += fx * simDt;
        edge.from.vy += fy * simDt;
      }
      if (!edge.to.fixed) {
        edge.to.vx -= fx * simDt;
        edge.to.vy -= fy * simDt;
      }
    }

    for (const node of packet.nodes) {
      if (node.fixed) continue;

      node.vx += (node.anchorX - node.x) * packet.anchorK * simDt;
      node.vy += (node.anchorY - node.y) * packet.anchorK * simDt + packet.gravity * simDt;

      node.vx *= packet.damping;
      node.vy *= packet.damping;

      node.x += node.vx * 60 * simDt;
      node.y += node.vy * 60 * simDt;

      const pad = packet.nodeRadius + 8;
      node.x = Math.max(packet.area.x + pad, Math.min(packet.area.x + packet.area.w - pad, node.x));
      node.y = Math.max(packet.area.y + pad, Math.min(packet.area.y + packet.area.h - pad, node.y));
    }

    if (packet.title) {
      chalkText(
        ctxs.ink,
        packet.title,
        packet.titleX,
        packet.titleY,
        packet.titleSize,
        0.95,
        'sans-serif',
        'center',
        { role: 'diagramTitle' }
      );
    }
    if (packet.subtitle) {
      chalkText(
        ctxs.ink,
        packet.subtitle,
        packet.titleX,
        packet.titleY + packet.titleSize + 8,
        packet.labelSize,
        0.72,
        'sans-serif',
        'center',
        { role: 'diagramSubtitle' }
      );
    }

    for (const edge of packet.edges) {
      chalkLine(ctxs.ink, edge.from.x, edge.from.y, edge.to.x, edge.to.y, 2.2, 0.55, {
        role: 'diagramEdge',
      });
    }

    for (const node of packet.nodes) {
      ctxs.ink.save();
      ctxs.ink.strokeStyle = nodeStrokeRole.color;
      ctxs.ink.fillStyle = nodeFillRole.color;
      ctxs.ink.lineWidth = 2.2;
      ctxs.ink.beginPath();
      ctxs.ink.arc(node.x, node.y, packet.nodeRadius, 0, Math.PI * 2);
      ctxs.ink.fill();
      ctxs.ink.stroke();
      ctxs.ink.restore();

      drawLabelBadge(
        ctxs.ink,
        node.label,
        node.x,
        node.y + packet.nodeRadius + packet.labelSize + 2,
        packet.labelSize
      );
    }

    return t < packet.durationSec;
  });
}
